import * as React from "react";
import { format, parseISO, isValid, startOfMonth, startOfWeek, subDays } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarBlank } from "@phosphor-icons/react";

import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * DatePicker — shadcn Popover + Calendar wrapper.
 *
 * Keeps the same contract as our old <Input type="date"> fields: `value` and
 * `onChange` both use `yyyy-MM-dd` ISO date strings, so existing state/API
 * code does not change.
 *
 * Quick shortcuts (Aujourd'hui / Hier / Début semaine / Début mois) are
 * rendered above the calendar for faster night-time entry — one tap sets the
 * most-common values without opening the month grid.
 */
export function DatePicker({ value, onChange, placeholder = "Choisir une date", testId, className, disabled = false }) {
  const [open, setOpen] = React.useState(false);
  const selected = React.useMemo(() => {
    if (!value) return undefined;
    const d = parseISO(value);
    return isValid(d) ? d : undefined;
  }, [value]);

  const pick = (d) => {
    if (d && isValid(d)) {
      onChange(format(d, "yyyy-MM-dd"));
      setOpen(false);
    }
  };

  const shortcuts = React.useMemo(() => {
    const today = new Date();
    return [
      { label: "Aujourd'hui", date: today, testId: "shortcut-today" },
      { label: "Hier", date: subDays(today, 1), testId: "shortcut-yesterday" },
      { label: "Cette semaine", date: startOfWeek(today, { weekStartsOn: 1 }), testId: "shortcut-week" },
      { label: "Ce mois", date: startOfMonth(today), testId: "shortcut-month" },
    ];
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-start text-left font-normal bg-[#09090b] border-[#27272a] text-white hover:bg-[#121217] hover:text-white",
            !selected && "text-[#71717a]",
            className,
          )}
        >
          <CalendarBlank size={16} className="mr-2 opacity-80" />
          {selected ? format(selected, "dd/MM/yyyy", { locale: fr }) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#121217] border-[#27272a] text-white" align="start">
        <div className="flex flex-wrap gap-1.5 p-3 pb-0">
          {shortcuts.map((s) => (
            <button
              key={s.label}
              type="button"
              data-testid={s.testId}
              onClick={() => pick(s.date)}
              className="px-3 py-1 rounded-full text-xs border border-[#27272a] bg-[#09090b] text-[#e4e4e7] hover:bg-[#fbbf24] hover:text-black hover:border-[#fbbf24] transition-colors"
            >
              {s.label}
            </button>
          ))}
        </div>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={pick}
          locale={fr}
          weekStartsOn={1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;

